import { Paper, Group, Text, Stack } from '@mantine/core';
import { ReactNode } from 'react';
import { StatusBadge } from '../../../common/StatusBadge';
import type { DisplayStatus } from '../../../../types/status';

function mapToneToStatus(tone: 'green' | 'blue' | 'yellow' | 'red' | 'gray'): DisplayStatus {
  switch (tone) {
    case 'green':
      return 'approved';
    case 'blue':
      return 'in_review';
    case 'yellow':
      return 'pending';
    case 'red':
      return 'needs_attention';
    case 'gray':
    default:
      return 'not_submitted';
  }
}

export function SafeTCardShell({
  title,
  subtitle,
  statusLabel,
  statusColor,
  statusTooltip,
  rightSlot,
  children,
}: {
  title: string;
  subtitle?: string;
  statusLabel: string;
  statusColor: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
  statusTooltip?: string;
  rightSlot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      p="md"
      radius="md"
      shadow="md"
      withBorder
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow:
            theme.colorScheme === 'dark'
              ? '0 0 16px rgba(255, 255, 255, 0.15)'
              : '0 8px 16px rgba(0, 0, 0, 0.1)',
        },
      })}
    >
      <Stack spacing="sm" sx={{ flex: 1, minHeight: 0 }}>
        <Group position="apart" align="flex-start" noWrap>
          <Stack spacing={4} style={{ minWidth: 0 }}>
            <Text
              size="lg"
              weight={900}
              sx={(theme) => ({
                color: theme.colorScheme === 'dark' ? theme.white : theme.black,
                lineHeight: 1.1,
              })}
            >
              {title}
            </Text>
            {subtitle ? (
              <Text size="xs" color="dimmed">
                {subtitle}
              </Text>
            ) : null}
          </Stack>
          <StatusBadge status={mapToneToStatus(statusColor)} label={statusLabel} tooltipLabel={statusTooltip} />
        </Group>

        {children}
      </Stack>
    </Paper>
  );
}
